import { useEffect, useState, useMemo } from "react";
import { api } from "../../lib/api";
import { Link } from "react-router-dom";
import {
  TrendingDown,
  TrendingUp,
  Sparkles,
  Activity,
  Flame,
  PlusCircle,
  CheckCircle2,
  Repeat,
  ChevronRight,
} from "lucide-react";
import { MUSCLE_BY_ID, canonicalMuscleId } from "../../data/muscleAtlas";
import { MUSCLE_LABELS } from "../BodyDiagram";

/**
 * Iter Y — Expanded "What Changed Since Last Visit" full card.
 *
 * Reads from data the API already exposes:
 *   - GET /clients/:id/body-chart-timeline   (sessions[], recurring_muscles[])
 *   - GET /clients/:id/previous-session      (latest completed session)
 * and synthesizes a complete client-readable progress diff card.
 *
 * Surfaces four signal bands per card:
 *   1. Headline KPI row — Pain Δ · ROM Δ · improved areas · new findings
 *   2. Pain trajectory sparkline (last N sessions)
 *   3. Quadrants — Improving, Stable, Newly Added, Recurring (≥3 sessions)
 *   4. Therapist's own progress notes (better / worse / unchanged)
 *
 * Designed to be embedded on:
 *   • ClientDetail (overview card)
 *   • NewSession pre-treatment phase (so therapist sees context up front)
 *   • Client Progress Portal (read-only public view)
 *
 * Props:
 *   clientId        required
 *   sessionId?      session to compare against the previous one. If omitted,
 *                   we use the most-recent completed session as "current".
 *   variant         "full" | "compact" | "client"  (client = no medical jargon)
 *   testIdPrefix
 */
export default function WhatChangedCard({
  clientId,
  sessionId = null,
  variant = "full",
  testIdPrefix = "what-changed",
}) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get(`/clients/${clientId}/body-chart-timeline`),
      sessionId
        ? api.get(`/sessions/${sessionId}`).catch(() => ({ data: null }))
        : Promise.resolve({ data: null }),
    ])
      .then(([t, s]) => {
        if (cancelled) return;
        setData({ timeline: t.data, currentSession: s.data });
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId, sessionId]);

  const analysis = useMemo(() => analyze(data), [data]);

  if (loading) {
    return (
      <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8" data-testid={`${testIdPrefix}-loading`}>
        <div className="h-4 w-40 bg-paper-100 rounded mb-3 animate-pulse" />
        <div className="h-3 w-72 bg-paper-100 rounded animate-pulse" />
      </div>
    );
  }

  if (!analysis || analysis.sessions.length < 1) {
    return null;
  }

  if (analysis.sessions.length < 2) {
    return (
      <div className="bg-paper-50 border border-paper-200 rounded-3xl p-6" data-testid={`${testIdPrefix}-onefirst`}>
        <div className="label-overline text-sage-600 mb-1">What changed since last visit</div>
        <p className="text-sm text-ink-700">
          This is the first completed session — comparisons appear once we have a baseline + a follow-up.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-paper-200 rounded-3xl overflow-hidden" data-testid={`${testIdPrefix}-card`}>
      {/* Header strip */}
      <div className="px-6 sm:px-8 pt-6 pb-4 border-b border-paper-100 bg-gradient-to-b from-sage-500/5 to-transparent">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="label-overline text-sage-600 mb-1 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> What changed since last visit
            </div>
            <h3 className="font-heading text-2xl text-ink-900" data-testid={`${testIdPrefix}-headline`}>
              {analysis.headline}
            </h3>
          </div>
          <div className="text-xs text-ink-500 text-right" data-testid={`${testIdPrefix}-dates`}>
            <div>Session #{analysis.current.session_num} · {formatDate(analysis.current.date)}</div>
            <div>compared to #{analysis.previous.session_num} · {formatDate(analysis.previous.date)}</div>
          </div>
        </div>
      </div>

      <div className="p-6 sm:p-8 space-y-6">
        {/* KPI row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid={`${testIdPrefix}-kpis`}>
          <KpiTile
            label="Pain"
            value={analysis.painDeltaLabel}
            sub={analysis.painPctSub}
            accent={analysis.painAccent}
            icon={analysis.painDelta >= 1 ? TrendingDown : TrendingUp}
            testid={`${testIdPrefix}-kpi-pain`}
          />
          <KpiTile
            label="ROM gains"
            value={analysis.romGainCount > 0 ? `${analysis.romGainCount} ↑` : "No measurements"}
            sub={analysis.romGainAvgLabel}
            accent={analysis.romGainCount > 0 ? "sage" : "ink"}
            icon={Activity}
            testid={`${testIdPrefix}-kpi-rom`}
          />
          <KpiTile
            label="Newly added"
            value={analysis.newAreas.length}
            sub="muscles new to plan"
            accent={analysis.newAreas.length > 0 ? "amber" : "ink"}
            icon={PlusCircle}
            testid={`${testIdPrefix}-kpi-new`}
          />
          <KpiTile
            label="Recurring"
            value={analysis.recurring.length}
            sub="areas worked ≥3×"
            accent={analysis.recurring.length > 0 ? "terracotta" : "ink"}
            icon={Repeat}
            testid={`${testIdPrefix}-kpi-recurring`}
          />
        </div>

        {/* Pain sparkline */}
        {analysis.painSeries.length >= 2 && (
          <div className="bg-paper-50 border border-paper-200 rounded-2xl p-4" data-testid={`${testIdPrefix}-pain-sparkline`}>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="label-overline text-ink-700">Pain trajectory</div>
              <div className="text-xs text-ink-500">Last {analysis.painSeries.length} session{analysis.painSeries.length === 1 ? "" : "s"}</div>
            </div>
            <PainSparkline series={analysis.painSeries} />
          </div>
        )}

        {/* Four quadrants */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid={`${testIdPrefix}-quadrants`}>
          <Quadrant
            title="Improving areas"
            hint="Worked & pain trending down"
            icon={CheckCircle2}
            accent="sage"
            items={analysis.improvingAreas.map((m) => ({ id: m.id, label: muscleLabel(m.id), meta: m.meta }))}
            emptyHint="No improving trend captured yet."
            testid={`${testIdPrefix}-improving`}
          />
          <Quadrant
            title="Recurring patterns"
            hint="Worked across 3+ sessions"
            icon={Flame}
            accent="terracotta"
            items={analysis.recurring.map((m) => ({ id: m.id, label: muscleLabel(m.id), meta: m.meta }))}
            emptyHint="Nothing recurring yet."
            testid={`${testIdPrefix}-recurring-list`}
          />
          <Quadrant
            title="New this visit"
            hint="Areas added to the plan this session"
            icon={PlusCircle}
            accent="amber"
            items={analysis.newAreas.map((id) => ({ id, label: muscleLabel(id) }))}
            emptyHint="No new areas — continuing established plan."
            testid={`${testIdPrefix}-new`}
          />
          <Quadrant
            title="No longer in the plan"
            hint="Worked last time, not this time"
            icon={ChevronRight}
            accent="ink"
            items={analysis.resolvedAreas.map((id) => ({ id, label: muscleLabel(id) }))}
            emptyHint="No areas dropped."
            testid={`${testIdPrefix}-resolved`}
          />
        </div>

        {/* Therapist progress notes */}
        {variant !== "client" && analysis.progressNotes && hasAnyProgressNote(analysis.progressNotes) && (
          <div className="border-t border-paper-100 pt-4" data-testid={`${testIdPrefix}-progress-notes`}>
            <div className="label-overline mb-3 text-ink-700">Therapist's progress notes</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              {analysis.progressNotes.better && (
                <ProgressNote label="Better" tone="sage" text={analysis.progressNotes.better} testid={`${testIdPrefix}-note-better`} />
              )}
              {analysis.progressNotes.worse && (
                <ProgressNote label="Worse" tone="terracotta" text={analysis.progressNotes.worse} testid={`${testIdPrefix}-note-worse`} />
              )}
              {analysis.progressNotes.unchanged && (
                <ProgressNote label="Unchanged" tone="ink" text={analysis.progressNotes.unchanged} testid={`${testIdPrefix}-note-unchanged`} />
              )}
              {analysis.progressNotes.new_symptoms && (
                <ProgressNote label="New" tone="amber" text={analysis.progressNotes.new_symptoms} testid={`${testIdPrefix}-note-new`} />
              )}
              {analysis.progressNotes.resolved_symptoms && (
                <ProgressNote label="Resolved" tone="sage" text={analysis.progressNotes.resolved_symptoms} testid={`${testIdPrefix}-note-resolved`} />
              )}
            </div>
          </div>
        )}

        {variant === "full" && (
          <div className="pt-2">
            <Link
              to={`/clients/${clientId}/body-chart`}
              className="inline-flex items-center gap-2 min-h-[40px] px-4 rounded-full bg-terracotta-500/10 text-terracotta-700 hover:bg-terracotta-500/15 text-sm font-medium"
              data-testid={`${testIdPrefix}-open-body-chart`}
            >
              <Flame className="w-4 h-4" /> Open body chart history <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────
function muscleLabel(id) {
  return MUSCLE_LABELS[id]
    || MUSCLE_BY_ID[canonicalMuscleId(id)]?.label
    || id;
}

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

function hasAnyProgressNote(notes) {
  return notes && Object.values(notes).some((v) => v && String(v).trim().length > 0);
}

/**
 * Computes the analytical payload from raw API responses. Pure function so
 * the card stays a thin renderer.
 */
function analyze(input) {
  if (!input || !input.timeline) return null;
  const sessions = input.timeline.sessions || [];
  if (sessions.length === 0) return null;
  const heatmap = input.timeline.heatmap || {};

  // Find the "current" session (sessionId override or most recent)
  const current = (input.currentSession
    && sessions.find((s) => s.id === input.currentSession.id))
    || sessions[sessions.length - 1];
  const currentIdx = current ? sessions.indexOf(current) : -1;
  const previous = currentIdx > 0 ? sessions[currentIdx - 1] : null;

  if (!previous || !current) {
    return { sessions, current, previous: null };
  }

  // Pain
  const pb = num(current.pain_before);
  const pa = num(current.pain_after);
  const painDelta = pb != null && pa != null ? pb - pa : null;
  const painPct = pb != null && pa != null && pb > 0 ? Math.round(((pb - pa) / pb) * 100) : null;
  const painDeltaLabel = painDelta == null
    ? "—"
    : painDelta > 0 ? `↓ ${painDelta} pt${painDelta === 1 ? "" : "s"}`
    : painDelta < 0 ? `↑ ${Math.abs(painDelta)} pt${Math.abs(painDelta) === 1 ? "" : "s"}`
    : "no change";
  const painPctSub = painPct != null ? `${Math.abs(painPct)}% ${painPct >= 0 ? "improvement" : "increase"}` : "this session";
  const painAccent = painDelta == null
    ? "ink"
    : painDelta > 0 ? "sage" : painDelta < 0 ? "terracotta" : "ink";

  // ROM (from currentSession outcome rom_measurements)
  const rom = (input.currentSession?.outcome?.rom_measurements) || [];
  const gains = rom
    .map((r) => (r.before != null && r.after != null && Number(r.before) > 0
      ? { label: r.label, gain: Math.round(((Number(r.after) - Number(r.before)) / Number(r.before)) * 100) }
      : null))
    .filter(Boolean);
  const romGainCount = gains.filter((g) => g.gain > 0).length;
  const romGainAvg = gains.length ? Math.round(gains.reduce((s, g) => s + g.gain, 0) / gains.length) : null;
  const romGainAvgLabel = romGainAvg != null ? `${romGainAvg > 0 ? "+" : ""}${romGainAvg}% avg` : "—";

  // Areas — set difference of worked muscles current vs previous
  const curSet = new Set((current.worked_areas || []).map(canonicalMuscleId));
  const prevSet = new Set((previous.worked_areas || []).map(canonicalMuscleId));
  const newAreas = [...curSet].filter((id) => !prevSet.has(id));
  const resolvedAreas = [...prevSet].filter((id) => !curSet.has(id));

  // Improving — recurring muscles (≥2 sessions touched) whose heat trend is "improving"
  const improvingAreas = Object.entries(heatmap)
    .filter(([id, h]) => h.trend === "improving" && curSet.has(id))
    .sort(([, a], [, b]) => b.intensity - a.intensity)
    .map(([id, h]) => ({ id, meta: `${h.count}× · improving` }));

  const recurring = (input.timeline.recurring_muscles || []).map((id) => ({
    id,
    meta: `${heatmap[id]?.count || 0}×`,
  }));

  // Pain sparkline — last 6 sessions, prefer pain_after as the recorded point
  const painSeries = sessions
    .slice(Math.max(0, currentIdx - 5), currentIdx + 1)
    .map((s) => ({ num: s.session_num, value: num(s.pain_after) }))
    .filter((p) => p.value != null);

  // Therapist progress notes from current session (if accessible)
  const progressNotes = input.currentSession?.progress_since_last || null;

  // Headline
  let headline = "Session captured";
  if (painDelta != null && painDelta >= 2) {
    headline = `Pain dropped ${painDelta} point${painDelta === 1 ? "" : "s"}${painPct != null ? ` (${painPct}%)` : ""}`;
  } else if (painDelta != null && painDelta <= -1) {
    headline = `Pain ticked up ${Math.abs(painDelta)} point${Math.abs(painDelta) === 1 ? "" : "s"} — adjust the plan`;
  } else if (romGainCount >= 2) {
    headline = `${romGainCount} range-of-motion gains today`;
  } else if (newAreas.length > 0) {
    headline = `${newAreas.length} new area${newAreas.length === 1 ? "" : "s"} added this visit`;
  } else if (improvingAreas.length > 0) {
    headline = `Continuing momentum on ${improvingAreas.length} improving area${improvingAreas.length === 1 ? "" : "s"}`;
  }

  return {
    sessions, current, previous,
    headline,
    painDelta, painPct, painDeltaLabel, painPctSub, painAccent,
    romGainCount, romGainAvg, romGainAvgLabel,
    newAreas, resolvedAreas, improvingAreas, recurring,
    painSeries, progressNotes,
  };
}

function num(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// ── Atomic UI bits ─────────────────────────────────────────────────────
function KpiTile({ label, value, sub, accent = "ink", icon: Icon, testid }) {
  const accentText =
    accent === "sage" ? "text-sage-700" :
    accent === "terracotta" ? "text-terracotta-700" :
    accent === "amber" ? "text-amber-700" :
    "text-ink-900";
  const accentRing =
    accent === "sage" ? "bg-sage-500/10" :
    accent === "terracotta" ? "bg-terracotta-500/10" :
    accent === "amber" ? "bg-amber-500/10" :
    "bg-paper-100";
  return (
    <div className="bg-paper-50 border border-paper-200 rounded-2xl p-3" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <span className="label-overline text-ink-500">{label}</span>
        <span className={`w-6 h-6 rounded-full inline-flex items-center justify-center ${accentRing}`}>
          {Icon && <Icon className={`w-3.5 h-3.5 ${accentText}`} />}
        </span>
      </div>
      <div className={`font-heading text-2xl ${accentText}`}>{value}</div>
      {sub && <div className="text-xs text-ink-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Quadrant({ title, hint, icon: Icon, accent, items, emptyHint, testid }) {
  const accentBorder =
    accent === "sage" ? "border-sage-500/30" :
    accent === "terracotta" ? "border-terracotta-500/30" :
    accent === "amber" ? "border-amber-500/30" :
    "border-paper-200";
  const accentChip =
    accent === "sage" ? "bg-sage-500/10 text-sage-700" :
    accent === "terracotta" ? "bg-terracotta-500/10 text-terracotta-700" :
    accent === "amber" ? "bg-amber-500/10 text-amber-700" :
    "bg-paper-100 text-ink-700";
  return (
    <div className={`bg-white border rounded-2xl p-4 ${accentBorder}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon className="w-4 h-4 text-ink-700" />}
        <h4 className="text-sm font-semibold text-ink-900">{title}</h4>
      </div>
      <p className="text-xs text-ink-500 mb-3">{hint}</p>
      {items.length === 0 ? (
        <p className="text-xs text-ink-500 italic" data-testid={`${testid}-empty`}>{emptyHint}</p>
      ) : (
        <ul className="flex flex-wrap gap-1.5" data-testid={`${testid}-items`}>
          {items.map((it) => (
            <li
              key={it.id}
              className={`text-xs px-2.5 py-1 rounded-full inline-flex items-center gap-1.5 ${accentChip}`}
              data-testid={`${testid}-item-${it.id}`}
            >
              {it.label}
              {it.meta && <span className="opacity-70">· {it.meta}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ProgressNote({ label, tone, text, testid }) {
  const toneCls =
    tone === "sage" ? "border-sage-500/30 bg-sage-500/5 text-sage-800" :
    tone === "terracotta" ? "border-terracotta-500/30 bg-terracotta-500/5 text-terracotta-800" :
    tone === "amber" ? "border-amber-500/30 bg-amber-500/5 text-amber-800" :
    "border-paper-200 bg-paper-50 text-ink-800";
  return (
    <div className={`border rounded-xl p-3 ${toneCls}`} data-testid={testid}>
      <div className="label-overline mb-1 text-current opacity-70">{label}</div>
      <p className="text-sm leading-snug">{text}</p>
    </div>
  );
}

function PainSparkline({ series }) {
  if (series.length < 2) return null;
  const max = Math.max(10, ...series.map((p) => p.value));
  const w = 320;
  const h = 64;
  const padX = 6;
  const padY = 4;
  const stepX = (w - padX * 2) / Math.max(1, series.length - 1);
  const pts = series.map((p, i) => ({
    x: padX + i * stepX,
    y: padY + (h - padY * 2) * (1 - p.value / max),
    value: p.value,
    num: p.num,
  }));
  const pathD = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");
  const last = pts[pts.length - 1];
  const first = pts[0];
  const trendingDown = last.value < first.value;
  const stroke = trendingDown ? "#7E8E63" : last.value > first.value ? "#C8553D" : "#A07C42";
  return (
    <svg viewBox={`0 0 ${w} ${h + 16}`} className="w-full max-w-md" aria-label="Pain trajectory">
      {/* horizontal target line at 0 */}
      <line x1={padX} y1={h - padY} x2={w - padX} y2={h - padY} stroke="#E5DDD0" strokeWidth="1" strokeDasharray="2 3" />
      <path d={pathD} fill="none" stroke={stroke} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      {pts.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r={i === pts.length - 1 ? 3.5 : 2} fill={stroke} />
          <text x={p.x} y={h + 12} fontSize="9" textAnchor="middle" fill="#6E6557">{p.num}</text>
        </g>
      ))}
      {/* Annotate first + last values */}
      <text x={first.x} y={first.y - 6} fontSize="10" textAnchor="middle" fill="#3F3A33" fontWeight="600">{first.value}</text>
      <text x={last.x} y={last.y - 6} fontSize="10" textAnchor="middle" fill={stroke} fontWeight="700">{last.value}</text>
    </svg>
  );
}
